import React from 'react'
import clsx from 'clsx'

import { GridColumnProps } from './GridColumn.types'
import styles from './GridColumn.module.scss'

/**
 * GridColumn component for positioning content within the 12-column Grid
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child elements to render
 * @param {string} [props.className] - Additional CSS class names
 * @param {number} [props.span=12] - Number of columns to span (1-12)
 * @param {number} [props.start] - Starting column (1-12)
 * @param {number} [props.sm] - Column span for small devices (mobile)
 * @param {number} [props.md] - Column span for medium devices (tablet)
 * @param {number} [props.lg] - Column span for large devices (desktop)
 * @param {number} [props.xl] - Column span for extra large devices
 */
export default function GridColumn({
  children,
  className = '',
  span = 12,
  start,
  xs,
  sm,
  md,
  lg,
  xl,
}: GridColumnProps) {
  // Validate span is between 1 and 12
  const validSpan = Math.min(Math.max(1, span), 12)

  // Calculate responsive classes
  const responsiveClasses = [
    sm ? styles[`sm${sm}`] : '',
    xs ? styles[`xs${xs}`] : '',
    md ? styles[`md${md}`] : '',
    lg ? styles[`lg${lg}`] : '',
    xl ? styles[`xl${xl}`] : '',
  ]
    .filter(Boolean)
    .join(' ')

  // Calculate column start position
  const columnStyle = {} as { gridColumnStart: number }
  if (start) {
    columnStyle.gridColumnStart = Math.min(Math.max(1, start), 12)
  }

  return (
    <div
      className={clsx(
        styles.column,
        styles[`span${validSpan}`],
        responsiveClasses,
        columnStyle,
        className
      )}
    >
      {children}
    </div>
  )
}
